import { cssVars } from '@lmc/design-system/tokens'
import { Heading } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Input, Modal } from 'antd'
import useRequestSecondSignatureCode from 'hooks/useRequestSecondSignatureCode'
import useResendSecondSignatureCode from 'hooks/useResendSecondSignatureCode'
import type { TFunction } from 'i18next'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

const CODE_LENGTH = 6
const COUNTDOWN_TICK_MS = 1000

export type VerificationCodeModalProps = {
  open: boolean
  signerId: string
  signerName: string
  // Pattern's _id when editing, generated residentMedicationId when adding.
  subjectId: string
  onCancel: () => void
  // The host's real save call with the code attached — there's no separate
  // verify endpoint. Reject with an Error whose message is shown verbatim.
  onConfirm: (code: string) => Promise<void>
  testId?: string
}

const LOCKED_ERROR_CODE = 'second_signature_locked'

// ApiError copies the response body's fields onto itself (apiError.ts), so
// this duck-types the same way other callers in the app do.
const getErrorCode = (e: unknown): string | undefined =>
  e && typeof e === 'object' && 'code' in e
    ? String((e as { code?: unknown }).code)
    : undefined

// fetch() itself (not an HTTP error response — that becomes an ApiError)
// rejects with a bare TypeError when the request never reached the network,
// and the browser's own wording for that ("Failed to fetch", "NetworkError
// when attempting to fetch resource.", "Load failed") isn't fit to show a user.
const isNetworkError = (e: unknown): boolean =>
  e instanceof TypeError &&
  /Failed to fetch|NetworkError|Load failed/i.test(e.message)

const getDisplayErrorMessage = (
  e: unknown,
  t: TFunction<string, undefined>
): string => {
  if (isNetworkError(e)) {
    return t(emarV2Constants.SECOND_SIGNATURE_MODAL_OFFLINE_ERROR)
  }
  const message = e instanceof Error ? e.message : undefined
  return message ?? t(emarV2Constants.SECOND_SIGNATURE_MODAL_GENERIC_ERROR)
}

const formatCountdown = (ms: number) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const VerificationCodeModal = ({
  open,
  signerId,
  signerName,
  subjectId,
  onCancel,
  onConfirm,
  testId
}: VerificationCodeModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { requestCode } = useRequestSecondSignatureCode()
  const { resendCode, isLoading: isResending } = useResendSecondSignatureCode()

  const [maskedEmail, setMaskedEmail] = useState('')
  const [alreadySent, setAlreadySent] = useState(false)
  const [resentJustNow, setResentJustNow] = useState(false)
  const [resendAvailableAt, setResendAvailableAt] = useState<string>()
  const [isLoadingInitial, setIsLoadingInitial] = useState(true)
  // True once a code has actually been delivered (this request or a prior
  // one reused by the server) — gates the "we've sent a code to..." body
  // copy, which would otherwise show a blank recipient email before that.
  const [hasSentCode, setHasSentCode] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string>()
  const [isLocked, setIsLocked] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  // Re-fetches whenever the modal (re)opens for a signer — the server already
  // knows if a code was reused or locked, so the modal doesn't track that itself.
  // biome-ignore lint/correctness/useExhaustiveDependencies: requestCode excluded on purpose, see above
  useEffect(() => {
    if (!open) {
      return
    }
    let cancelled = false
    setCode('')
    setError(undefined)
    setResentJustNow(false)
    setIsLocked(false)
    setAlreadySent(false)
    setMaskedEmail('')
    setResendAvailableAt(undefined)
    setIsLoadingInitial(true)
    setHasSentCode(false)

    requestCode({ signerId, subjectId })
      .then((response) => {
        if (cancelled) {
          return
        }
        setMaskedEmail(response.maskedEmail)
        setResendAvailableAt(response.resendAvailableAt)
        setAlreadySent(response.reused && !response.invalidated)
        setIsLocked(response.reused && response.invalidated)
        setHasSentCode(true)
      })
      .catch((e) => {
        if (cancelled) {
          return
        }
        setError(getDisplayErrorMessage(e, t))
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingInitial(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [open, signerId, subjectId])

  useEffect(() => {
    if (!open) {
      return
    }
    const interval = setInterval(() => setNow(Date.now()), COUNTDOWN_TICK_MS)
    return () => clearInterval(interval)
  }, [open])

  const msUntilResend = resendAvailableAt
    ? new Date(resendAvailableAt).getTime() - now
    : 0
  const canResend = msUntilResend <= 0

  const handleChangeCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
    setError(undefined)
  }

  const handleConfirm = async () => {
    if (code.length !== CODE_LENGTH) {
      setError(t(emarV2Constants.SECOND_SIGNATURE_MODAL_INCOMPLETE_ERROR))
      return
    }

    setIsConfirming(true)
    try {
      await onConfirm(code)
    } catch (e) {
      setError(getDisplayErrorMessage(e, t))
      setCode('')
      if (getErrorCode(e) === LOCKED_ERROR_CODE) {
        setIsLocked(true)
      }
    } finally {
      setIsConfirming(false)
    }
  }

  const handleResend = async () => {
    try {
      const response = await resendCode({ signerId, subjectId })
      if (response.status === 'sent') {
        setMaskedEmail(response.maskedEmail)
        setResendAvailableAt(response.resendAvailableAt)
        setAlreadySent(false)
        setResentJustNow(true)
        setCode('')
        setError(undefined)
        setIsLocked(false)
        setHasSentCode(true)
      } else {
        setResendAvailableAt(response.resendAvailableAt)
      }
    } catch (e) {
      setError(getDisplayErrorMessage(e, t))
    }
  }

  const bodyTestId = testId ? `${testId}-verification-modal` : undefined

  return (
    <Modal
      open={open}
      onCancel={isConfirming ? undefined : onCancel}
      footer={null}
      width={580}
      destroyOnClose
      closable={!isConfirming}
      keyboard={!isConfirming}
      maskClosable={!isConfirming}
      data-testid={bodyTestId}
    >
      <StyledHeading level={3}>
        {t(emarV2Constants.SECOND_SIGNATURE_MODAL_TITLE)}
      </StyledHeading>
      <Divider />
      <Content>
        {hasSentCode && (
          <Text>
            {alreadySent ? (
              <Trans
                ns={NAMESPACE.EMAR_V2}
                i18nKey={
                  emarV2Constants.SECOND_SIGNATURE_MODAL_ALREADY_SENT_BODY
                }
                values={{ name: signerName }}
                components={{ strong: <strong /> }}
              />
            ) : (
              <Trans
                ns={NAMESPACE.EMAR_V2}
                i18nKey={emarV2Constants.SECOND_SIGNATURE_MODAL_BODY}
                values={{ name: signerName, maskedEmail }}
                components={{ strong: <strong /> }}
              />
            )}
          </Text>
        )}

        {resentJustNow && (
          <InfoBanner
            data-testid={testId ? `${testId}-resent-banner` : undefined}
          >
            {t(emarV2Constants.SECOND_SIGNATURE_MODAL_RESENT_BODY)}
          </InfoBanner>
        )}

        <Field>
          <FieldLabel>
            {t(emarV2Constants.SECOND_SIGNATURE_MODAL_CODE_LABEL)}
          </FieldLabel>
          <Input
            value={code}
            onChange={handleChangeCode}
            inputMode="numeric"
            // No maxLength: the native HTML attribute truncates a pasted
            // string (e.g. "3 5 1 5 1 4") to 6 raw characters before
            // handleChangeCode can strip the spaces, losing digits
            // (ROST-3936). handleChangeCode enforces the length instead.
            status={error || isLocked ? 'error' : undefined}
            disabled={isLocked || isConfirming}
            placeholder={t(emarV2Constants.TYPE_HERE)}
            aria-label={t(emarV2Constants.SECOND_SIGNATURE_MODAL_CODE_LABEL)}
            data-testid={testId ? `${testId}-code-input` : undefined}
          />
          <HelperText $isError={Boolean(error) || isLocked}>
            {error ??
              (isLocked
                ? t(emarV2Constants.SECOND_SIGNATURE_MODAL_LOCKED_ERROR)
                : t(emarV2Constants.SECOND_SIGNATURE_MODAL_HELPER))}
          </HelperText>
        </Field>
      </Content>
      <Divider />
      <FooterRow>
        {isLoadingInitial ? null : canResend ? (
          <Button
            variant="text"
            onClick={handleResend}
            disabled={isResending}
            data-testid={testId ? `${testId}-resend` : undefined}
          >
            {t(emarV2Constants.SECOND_SIGNATURE_MODAL_RESEND_LINK)}
          </Button>
        ) : (
          <Text>
            {t(emarV2Constants.SECOND_SIGNATURE_MODAL_RESEND_COUNTDOWN, {
              time: formatCountdown(msUntilResend)
            })}
          </Text>
        )}
        <ButtonGroup>
          <Button
            onClick={onCancel}
            disabled={isConfirming}
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            {t(emarV2Constants.CANCEL)}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isLocked || isConfirming}
            isLoading={isConfirming}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {t(emarV2Constants.SECOND_SIGNATURE_MODAL_CONFIRM)}
          </Button>
        </ButtonGroup>
      </FooterRow>
    </Modal>
  )
}

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const Content = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const InfoBanner = styled.div`
  width: 100%;
  padding: 8px 16px;
  border-radius: 4px;
  background: ${cssVars.color.bg.status.info.subtle};
  border: 1px solid ${cssVars.color.border.status.info};
  color: ${cssVars.color.text.status.info};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 400px;
`

const FieldLabel = styled(Text)`
  color: ${({ theme }) => theme.color?.neutral?.color4};
`

const HelperText = styled(Text)<{ $isError: boolean }>`
  color: ${({ $isError, theme }) =>
    $isError ? theme.color?.status?.danger : theme.color?.neutral?.color4};
`

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
`

export default VerificationCodeModal
